import { configService, gtmService } from '$lib/services';

import type { Experiment } from '@api/types';
import { Cookie } from '@shared/cookies';
import { ExperimentGroup, ExperimentType } from '@shared/experiment';
import type { Cookies } from '@sveltejs/kit';
import { getCookie, setCookie } from '@utils/cookies';

import { browser } from '$app/environment';

type Params = {
    isUserExist: boolean;
    experiments: Experiment[];
    server?: {
        url: URL;
        cookies: Cookies;
    };
};

function setCookies({ isUserExist, experiments, server }: Params): void {
    const url = server?.url || (browser && new URL(window.location.href)) || null;
    const searchParams: [string, string][] = [...url?.searchParams.entries() ?? []];
    // force experiments
    const qaExperiments: string[] = [];
    if (searchParams.length) {
        const expTypes = Object.values(ExperimentType);
        const expGroups = Object.values(ExperimentGroup);
        searchParams.forEach(([k, v]) => {
            const r = /^exp_(.*)_group$/.exec(k);
            const t = r?.length && (r[1].toUpperCase() as ExperimentType) || null;
            const g = v && (v.toLowerCase() as ExperimentGroup) || null;
            if (t && g && expTypes.includes(t) && expGroups.includes(g)) {
                qaExperiments.push(`${t}=${g}`);
            }
        });
        setCookie(
            Cookie.QaExperiments,
            qaExperiments.length > 0 ? qaExperiments.join(',') : null,
            server?.cookies
        );
    }

    if (!qaExperiments.length && !isUserExist && experiments.length) {
        setCookie(Cookie.Experiments, JSON.stringify(experiments), server?.cookies);
    }
}

async function mergeCookiesAndConfig({ isUserExist, experiments }: Omit<Params, 'server'>): Promise<void> {
    if (!browser) {
        throw new Error('New experiments can be merged on the client');
    }

    // do nothing if force experiments
    if (getCookie(Cookie.QaExperiments)) {
        return;
    }

    let mergedExperiments = configService.get('experiments') ?? [];
    for (const e of experiments) {
        mergedExperiments = mergedExperiments.filter(me => me.type !== e.type);
        mergedExperiments.push(e);
    }

    configService.update({ experiments: mergedExperiments });
    const mergedExperimentKeys = mergedExperiments.map(e => `${e.type}_${e.v}_${e.g}`.toUpperCase());
    await gtmService().addData({ experiments: mergedExperimentKeys });

    if (!isUserExist) {
        setCookie(Cookie.Experiments, JSON.stringify(mergedExperiments));
    }
}

export const experimentService = { setCookies, mergeCookiesAndConfig };
